import { resolveApiBase } from "@cadenza/io-utils";
import {
  getSession as fetchSession,
  login,
  logout,
  signup,
} from "@cadenza/shared-auth/client/auth-client";

const FREE_MODE_ENABLED = import.meta.env.VITE_FREE_MODE === "1";

const defaultSession = {
  status: FREE_MODE_ENABLED ? "ready" : "loading",
  authenticated: false,
  user: null,
  tier: FREE_MODE_ENABLED ? "free" : "unknown",
  csrfToken: null,
  error: null,
};

let sessionState = { ...defaultSession };
const listeners = new Set();
let initPromise;

const isRecoverableSessionError = (error) => {
  if (!error) {
    return false;
  }

  if (error instanceof TypeError || error instanceof SyntaxError || error instanceof EvalError) {
    return true;
  }

  const message = String(error?.message || error || "").toLowerCase();

  if (!message) {
    return false;
  }

  return (
    message.includes("network") ||
    message.includes("failed to fetch") ||
    message.includes("bad gateway") ||
    message.includes("unexpected token") ||
    message.includes("cors") ||
    message.includes("load failed")
  );
};

const notify = () => {
  for (const listener of listeners) {
    try {
      listener(sessionState);
    } catch (error) {
      console.error("Session listener failed", error);
    }
  }
};

const setSession = (updater) => {
  const nextState = typeof updater === "function" ? updater(sessionState) : updater;
  sessionState = { ...sessionState, ...nextState };
  notify();
  return sessionState;
};

const baseConfig = (apiBase) => ({
  base: apiBase || resolveApiBase("/api"),
});

const withCsrfConfig = (config) => {
  const csrfToken = sessionState.csrfToken;
  if (!csrfToken) {
    return config;
  }
  return { ...config, csrfToken };
};

const applyResponse = (response) => {
  if (response?.authenticated) {
    return setSession({
      status: "authenticated",
      authenticated: true,
      user: response.user ?? null,
      tier: response.tier ?? "cloud",
      csrfToken: response.csrfToken ?? null,
      error: null,
    });
  }

  const csrfToken = response?.csrfToken ?? null;
  const errorMessage = response?.error;
  const error = errorMessage ? new Error(errorMessage) : null;

  return setSession({
    status: error ? "error" : "ready",
    authenticated: false,
    user: null,
    tier: FREE_MODE_ENABLED ? "free" : "unknown",
    csrfToken,
    error,
  });
};

const ensureCsrfToken = (config) =>
  fetchSession(config)
    .then((response) => applyResponse(response))
    .catch((error) => {
      setSession((current) => ({
        ...current,
        status: "error",
        error,
      }));
      throw error;
    });

const performAuth = (action, config, payload) =>
  ensureCsrfToken(config)
    .then(() => action(withCsrfConfig(config), payload))
    .then((response) => {
      const next = applyResponse(response);
      if (!next.authenticated) {
        const failure =
          next.error ||
          new Error("Authentication failed. Please check your details and try again.");
        throw failure;
      }
      return next;
    })
    .catch((error) => {
      setSession({
        status: "error",
        authenticated: false,
        user: null,
        tier: FREE_MODE_ENABLED ? "free" : sessionState.tier,
        error,
      });
      throw error;
    });

const performLogout = (config) =>
  ensureCsrfToken(config)
    .then(() => logout(withCsrfConfig(config)))
    .then((response) => applyResponse(response))
    .catch((error) => {
      setSession({
        status: "error",
        authenticated: false,
        user: null,
        tier: FREE_MODE_ENABLED ? "free" : sessionState.tier,
        error,
      });
      throw error;
    });

export const initSession = (apiBase) => {
  if (initPromise) {
    return initPromise;
  }
  if (FREE_MODE_ENABLED) {
    sessionState = { ...sessionState, status: "ready", tier: "free" };
    notify();
    initPromise = Promise.resolve(sessionState);
    return initPromise;
  }
  sessionState = { ...sessionState, status: "loading", error: null };
  notify();
  initPromise = fetchSession(baseConfig(apiBase))
    .then((response) => applyResponse(response))
    .catch((error) => {
      const recoverable = isRecoverableSessionError(error);

      if (recoverable) {
        console.warn("[Auth] Failed to initialise session, falling back to free mode", error);
      } else {
        console.error("[Auth] Failed to initialise session", error);
      }

      setSession({
        status: recoverable ? "ready" : "error",
        authenticated: false,
        user: null,
        tier: recoverable ? "free" : FREE_MODE_ENABLED ? "free" : "unknown",
        csrfToken: null,
        error,
      });
      return sessionState;
    });
  return initPromise;
};

export const getSessionState = () => sessionState;

export const subscribeSession = (listener) => {
  if (typeof listener !== "function") {
    throw new TypeError("Session subscriber must be a function");
  }
  listeners.add(listener);
  listener(sessionState);
  return () => {
    listeners.delete(listener);
  };
};

export const upgradeToAuthed = (apiBase) => {
  const config = baseConfig(apiBase);
  return {
    login: (credentials) => performAuth(login, config, credentials),
    signup: (details) => performAuth(signup, config, details),
    logout: () => performLogout(config),
  };
};

export const resetToFreeMode = () => {
  sessionState = { ...defaultSession };
  notify();
};

export { FREE_MODE_ENABLED, isRecoverableSessionError };
